import React from "react";
import { Building2, Award, Users, Target, Linkedin, Mail } from "lucide-react";
import { getLoginUrl } from '@/utils/domainUtils';

function Nav() {
  const handleSignIn = () => {
    window.location.href = getLoginUrl();
  };

  const handleGetStarted = () => {
    window.location.href = '/get-started';
  };

  return (
    <nav className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <a href="/" className="flex items-center space-x-2">
            <div className="w-5 h-5 rounded-full bg-black flex-shrink-0"></div>
            <span className="text-xl font-bold text-gray-900 lowercase" style={{ letterSpacing: '-0.5px', fontFamily: 'Inter, sans-serif' }}>complin</span>
          </a>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm">
          <a href="/#features" className="text-gray-600 hover:text-black">Features</a>
          <a href="/#how" className="text-gray-600 hover:text-black">How it works</a>
          <a href="/#industries" className="text-gray-600 hover:text-black">Industries</a>
          <a href="/#pricing" className="text-gray-600 hover:text-black">Pricing</a>
          <a href="/#faq" className="text-gray-600 hover:text-black">FAQ</a>
          <a href="/about" className="text-gray-900 font-medium">About</a>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSignIn} className="px-3 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm transition-colors">Sign in</button>
          <button onClick={handleGetStarted} className="px-3 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm transition-colors">Get Started</button>
        </div>
      </div>
    </nav>
  );
}

export default function AboutUsPage() {
  return (
    <div className="min-h-screen bg-white text-black">
      <Nav />
      
      {/* Hero Section */}
      <section className="border-b bg-gradient-to-b from-white to-gray-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-4">About Complin</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We're building the future of compliance management for multi-site operations, powered by AI and built by industry experts.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 border-b">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Profile Image */}
            <div className="flex justify-center md:justify-start">
              <div className="relative">
                <img
                  src="/dhaval-chokshi-profile.jpg"
                  alt="Dhaval Chokshi - Founder & CEO of Complin"
                  className="w-64 h-64 rounded-2xl object-cover shadow-lg border-4 border-white"
                  onError={(e) => {
                    // Fallback to a generated avatar if image fails to load
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=Dhaval+Chokshi&size=256&background=6366f1&color=fff&bold=true`;
                  }}
                />
                <a
                  href="https://www.linkedin.com/in/djchokshi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-4 right-4 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
                  title="View LinkedIn Profile"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Profile Content */}
            <div>
              <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-primary-200 bg-primary-50 text-primary-700 mb-4">
                <Building2 className="w-3 h-3" /> Founder & CEO
              </div>
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">Dhaval Chokshi</h2>
              <p className="text-gray-600 leading-7 mb-6">
                Dhaval Chokshi is the founder and CEO of Complin, bringing extensive experience in the hospitality industry 
                to revolutionize compliance management. With a deep understanding of the operational challenges faced by 
                hotels, restaurants, and multi-site operations, Dhaval has dedicated his career to creating solutions that 
                simplify complex compliance requirements.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Hospitality Expertise</h3>
                    <p className="text-sm text-gray-600">
                      Years of hands-on experience managing operations, compliance, and guest services across 
                      various hospitality establishments. Deep knowledge of industry standards, regulations, and 
                      best practices that drive operational excellence.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Vision for Compliance</h3>
                    <p className="text-sm text-gray-600">
                      Recognizing the pain points of manual compliance tracking, Dhaval envisioned a platform that 
                      leverages AI to automate document analysis, track renewals, and maintain audit-ready evidence 
                      vaults—all while keeping teams focused on delivering exceptional guest experiences.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Industry-First Approach</h3>
                    <p className="text-sm text-gray-600">
                      Built Complin from the ground up with multi-site operations professionals in mind, ensuring every 
                      feature addresses real-world challenges across industries. From life safety compliance to vendor contract 
                      management, Complin is designed by someone who understands the operational needs of hospitality, retail, 
                      education, and other multi-site businesses inside and out.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-4">
                <a
                  href="https://www.linkedin.com/in/djchokshi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 border-b bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-semibold text-gray-900 mb-6 text-center">Our Mission</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Simplify Compliance</h3>
              <p className="text-sm text-gray-600 leading-6">
                We believe compliance shouldn't be a burden. Our AI-powered platform automates the tedious 
                work of tracking renewals, analyzing documents, and maintaining evidence—so your team can 
                focus on what matters most.
              </p>
            </div>
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Built for Multi-Site Operations</h3>
              <p className="text-sm text-gray-600 leading-6">
                Every feature in Complin is designed with multi-site operations in mind. From portfolio 
                management to vendor accountability, we understand the unique challenges you face across 
                industries and build solutions that actually work.
              </p>
            </div>
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Audit-Ready Always</h3>
              <p className="text-sm text-gray-600 leading-6">
                Never scramble for documents again. Our evidence vault keeps everything organized, searchable, 
                and exportable—ensuring you're always prepared for inspections, audits, and compliance reviews.
              </p>
            </div>
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Scale with Confidence</h3>
              <p className="text-sm text-gray-600 leading-6">
                Whether you're managing one location or hundreds, Complin scales with you. Our per-location 
                pricing model means you only pay for what you use, with unlimited users on every plan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold text-gray-900 mb-4">Ready to Transform Your Compliance?</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Join multi-site operations professionals who trust Complin to keep their operations compliant, organized, and audit-ready.
          </p>
          <div className="flex items-center justify-center gap-3">
            <a
              href="/get-started"
              className="px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
            >
              Get Started Free
            </a>
            <a
              href={getLoginUrl()}
              className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              Sign In
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t">
        <div className="max-w-7xl mx-auto px-4 py-8 text-sm text-gray-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Complin, Inc.</div>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-black">Privacy</a>
            <a href="/terms" className="hover:text-black">Terms</a>
            <a href="/security" className="hover:text-black">Security</a>
            <a href="/about" className="hover:text-black">About</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

